/**
 * Utility functions for Order Popup components
 * Matches Flutter's OrderPopupUtils
 */

export type PopupStep = 'main' | 'branch' | 'address' | 'carColor' | 'carType'
export type AddressPopupStep = 'noAddresses' | 'savedAddresses' | 'addNewAddress'

/**
 * Get header title based on current step
 */
export const getHeaderTitle = (step: PopupStep, t: (key: string) => string): string => {
  switch (step) {
    case 'main':
      return t('cs.order_details') || 'تفاصيل الطلب'
    case 'branch':
      return t('cs.choose_branch') || 'اختر الفرع'
    case 'address':
      return t('cs.delivery_address') || 'عنوان التوصيل'
    case 'carColor':
      return t('cs.choose_car_color') || 'اختر لون السيارة'
    case 'carType':
      return t('cs.choose_car_type') || 'اختر نوع السيارة'
    default:
      return t('cs.order_details') || 'تفاصيل الطلب'
  }
}

/**
 * Get button color based on enabled state
 */
export const getButtonColor = (isEnabled: boolean): string => {
  return isEnabled ? 'var(--brand-primary)' : '#D1D5DB'
}

/**
 * Get text color based on enabled state
 */
export const getTextColor = (isEnabled: boolean): string => {
  return isEnabled ? '#FFFFFF' : '#9CA3AF'
}

/**
 * Get border color based on selection state
 */
export const getBorderColor = (isSelected: boolean): string => {
  return isSelected ? 'var(--brand-primary)' : '#D1D5DB'
}

/**
 * Get border width based on selection state
 */
export const getBorderWidth = (isSelected: boolean): number => {
  return isSelected ? 2 : 1
}

/**
 * Get background color based on selection state
 */
export const getBackgroundColor = (isSelected: boolean): string => {
  return isSelected ? 'var(--brand-primary)' : '#FFFFFF'
}

/**
 * Get text color for selection state
 */
export const getTextColorForSelection = (isSelected: boolean): string => {
  return isSelected ? '#FFFFFF' : '#6B7280'
}

/**
 * Get arrow icon based on language direction
 */
export const getArrowIcon = (isRTL: boolean): 'left' | 'right' => {
  return isRTL ? 'left' : 'right'
}

const parseBooleanValue = (value: any): boolean | undefined => {
  if (value === undefined || value === null) {
    return undefined
  }

  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return value !== 0
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['true', '1', 'yes'].includes(normalized)) {
      return true
    }
    if (['false', '0', 'no'].includes(normalized)) {
      return false
    }
  }

  return undefined
}

/**
 * Check if branch is closed
 */
export const isBranchClosed = (branch: any): boolean => {
  if (!branch) {
    return true
  }

  const closedFlag = parseBooleanValue(branch.isClosed ?? branch.is_closed)
  const availabilityRaw = branch.is_available ?? branch.isAvailable ?? branch.available
  const availability = parseBooleanValue(availabilityRaw)
  const hasAvailabilityFlag = availabilityRaw !== undefined && availabilityRaw !== null

  const statusRaw = branch.status ?? branch.Status ?? ''
  const status =
    typeof statusRaw === 'string' ? statusRaw.trim().toLowerCase() : ''
  const isStatusClosed = status === 'closed'

  return (
    closedFlag === true ||
    isStatusClosed ||
    (hasAvailabilityFlag && availability === false)
  )
}

/**
 * Check if can continue with order
 */
export const canContinue = (params: {
  selectedOrderType: string | null
  selectedBranch: any
  selectedAddress: any
  carColor: string
  carBrand: string
  carNumber: string
  selectedTypeId: number
}): boolean => {
  const { selectedOrderType, selectedBranch, selectedAddress, carColor, carBrand, carNumber, selectedTypeId } = params

  // Must have order type selected
  if (!selectedOrderType) return false

  // Must have branch selected and not undefined
  if (!selectedBranch || !selectedBranch.id) return false
  if (selectedBranch.id.toString().toLowerCase() === 'undefined') return false

  // For delivery orders (typeId = 1), address is required
  if (selectedTypeId === 1 && !selectedAddress) {
    return false
  }

  // For car pickup orders (typeId = 4), car details are required
  if (selectedTypeId === 4) {
    if (!carColor || !carBrand || !carNumber) {
      return false
    }
  }

  return true
}

/**
 * Format phone number
 */
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length >= 10) {
    return `${cleaned.substring(0, 3)} ${cleaned.substring(3, 6)} ${cleaned.substring(6)}`
  }
  return phone
}

/**
 * Validate email
 */
export const isValidEmail = (email: string): boolean => {
  return /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)
}

/**
 * Validate phone
 */
export const isValidPhone = (phone: string): boolean => {
  return /^[0-9]{10,15}$/.test(phone.replace(/\s/g, ''))
}

/**
 * Get error message
 */
export const getErrorMessage = (error: string, t: (key: string) => string): string => {
  if (error.includes('network')) {
    return t('cs.network_error') || 'خطأ في الشبكة'
  } else if (error.includes('timeout')) {
    return t('cs.timeout_error') || 'انتهت مهلة الاتصال'
  } else if (error.includes('unauthorized')) {
    return t('cs.unauthorized_error') || 'غير مصرح'
  } else {
    return t('cs.general_error') || 'حدث خطأ'
  }
}






